// GraphQL documents for the peak bagging web pages. Plain strings — these go
// through the session-cookie fetch in api.ts, not the graphql-request client.

export const PEAK_LISTS_QUERY = `
  query PeakBaggingWebLists($enteredOnly: Boolean) {
    peakLists(enteredOnly: $enteredOnly) {
      id
      slug
      name
      description
      regionLabel
      countryCode
      webPath
      peaksCount
      attribution
      baggedCount
      verifiedBaggedCount
      highlightColor
      baggedBadgeUrl
      nonBaggedBadgeUrl
    }
  }
`

// The /user/peaks dashboard. One round trip for the whole page: the viewer's
// totals, their own lists, and the catalogue for the "Other" section.
//
// Two calls rather than one, because the filters belong to the Other section
// alone — `myLists` must not disappear when someone filters the catalogue down
// to one country. Both share context[:viewer_peak_scores], so the second call
// costs no extra progress lookup.
//
// The Other section is paged: there are 500+ lists, and `paginatedPeakLists`
// exists precisely because `peakLists` returns a bare array that shipped mobile
// builds decode as one. `myLists` stays unpaged — nobody is on 500 lists.
//
// Status and sort are resolved server-side (Peaks::PeakListFilter) rather than
// in the client: "completed" and progress ordering are decisions the server
// already owns, and a second implementation here could disagree with the
// filter the enum names.
//
// The fragment is written out in full rather than interpolated from a const:
// spec/graphql/peaks/web_queries_spec.rb validates these documents by reading
// this file, so a template-literal interpolation would leave it checking a
// string that is not GraphQL. Self-contained keeps that check honest.
export const MY_PEAKS_QUERY = `
  query PeakBaggingMyPeaks($countrySlug: String, $status: PeakListStatusEnum,
                          $sortBy: PeakListSortEnum, $page: Int) {
    myPeakStats {
      baggedCount
      verifiedCount
      highestPeak {
        name
        heightM: displayElevationMeters
        baggedPeak { firstBaggedAt }
        primaryPeakList {
          slug
          name
          webPath
          highlightColor
          baggedBadgeUrl
          nonBaggedBadgeUrl
        }
      }
    }
    myLists: peakLists(mineOnly: true, sortBy: PROGRESS) {
      ...PeakListCard
    }
    catalogue: paginatedPeakLists(countrySlug: $countrySlug, status: $status,
                                  sortBy: $sortBy, page: $page) {
      totalCount
      totalPages
      currentPage
      hasNextPage
      hasPreviousPage
      peakLists {
        ...PeakListCard
      }
    }
  }

  fragment PeakListCard on PeakList {
    id
    slug
    name
    webPath
    regionLabel
    locationLabel
    countries {
      slug
      name
    }
    peaksCount
    baggedCount
    verifiedBaggedCount
    mine
    highlightColor
    baggedBadgeUrl
    nonBaggedBadgeUrl
  }
`

export const PEAKS_FOR_LIST_QUERY = `
  query PeakBaggingWebListPeaks($slug: String!, $page: Int, $perPage: Int) {
    peaksForList(slug: $slug, page: $page, perPage: $perPage) {
      totalCount
      totalPages
      currentPage
      hasNextPage
      peakList {
        id
        slug
        name
        webPath
        description
        regionLabel
        peaksCount
        attribution
        baggedCount
        regionLabel
        highlightColor
        baggedBadgeUrl
        nonBaggedBadgeUrl
      }
      peaks {
        id
        dobihNumber
        name
        latitude
        longitude
        heightM: displayElevationMeters
        prominenceM
        county
        bagged
        altNames
        description
        website
        wikipedia
        elevationSource
        baggedPeak {
          firstBaggedAt
          source
          recording { id title }
        }
      }
    }
  }
`



// Every list with its pins, for the all-lists map at /peaks.
//
// One round trip rather than one per list: the page switches every list on by
// default, so 22 lazy fetches would be 22 requests before the map draws. The
// pin is deliberately thin — `mapPoints` carries an id, a name, two floats, a
// height, a county and a boolean — and the rest of a peak's detail is fetched
// only for the one peak the reader opens.
export const PEAK_LISTS_WITH_POINTS_QUERY = `
  query PeakBaggingAllListPoints {
    peakLists {
      id
      slug
      name
      regionLabel
      peaksCount
      baggedCount
      highlightColor
      baggedBadgeUrl
      nonBaggedBadgeUrl
      webPath
      countries { name slug }
      mapPoints {
        id
        name
        latitude
        longitude
        bagged
        heightM
        county
      }
    }
  }
`

// The rest of a peak, for the detail column on the all-lists map. The pins that
// page plots carry only what a pin and a list row need, so opening one fills in
// the facts the panel prints.
export const PEAK_DETAIL_QUERY = `
  query PeakBaggingPeakDetail($id: ID!) {
    amenityById(id: $id) {
      id
      dobihNumber
      name
      latitude
      longitude
      heightM: displayElevationMeters
      prominenceM
      county
      bagged
      altNames
      description
      website
      wikipedia
      elevationSource
      baggedPeak {
        firstBaggedAt
        source
        recording { id title }
      }
    }
  }
`

// Fetched lazily, for the one peak the user opened — never for the whole list.
// See Queries::Peaks::HikesSummitingPeak for why it is not a field on the peak.
export const HIKES_SUMMITING_PEAK_QUERY = `
  query PeakBaggingSummitingHikes($amenityId: ID!, $limit: Int) {
    hikesSummitingPeak(amenityId: $amenityId, limit: $limit) {
      distanceM
      otherPeaksCount
      hike {
        id
        name
        canonicalUrl
        distanceMeters
        elevationGainMeters
        difficulty
        averageRating
        ratingsCount
        primaryImageUrl
      }
    }
  }
`

// The lists this peak belongs to, with the viewer's progress on each — the
// "39/214 Wainwrights" cards. Fetched for the selected peak only: peakLists is
// an association per amenity, so asking for it across a 282-peak list would be
// 282 extra queries.
export const PEAK_LISTS_FOR_PEAK_QUERY = `
  query PeakBaggingPeakLists($id: ID!) {
    amenityById(id: $id) {
      id
      placeId
      peakLists {
        id
        slug
        name
        webPath
        peaksCount
        baggedCount
        highlightColor
      }
    }
  }
`

// Runs detection inline over just-imported recordings so the import can show a
// review. See Mutations::Peaks::DetectPeaksForRecordings for why this is not
// left to the background worker.
export const DETECT_PEAKS_FOR_RECORDINGS_MUTATION = `
  mutation PeakBaggingDetectImported($input: DetectPeaksForRecordingsInput!) {
    detectPeaksForRecordings(input: $input) {
      recordingsProcessed
      newlyBaggedCount
      alreadyBaggedCount
      newlyBagged { id name heightM: displayElevationMeters }
      listsTouched { slug name }
    }
  }
`

// A list's leaderboard: the GetPeakLeaderboard the apps send, field for field. Written out in
// full for web_queries_spec.rb, like the documents above.
export const PEAK_LEADERBOARD_QUERY = `
  query PeakBaggingLeaderboard($scopeType: String, $scopeKey: String, $period: String,
                               $page: Int, $perPage: Int) {
    peakLeaderboard(scopeType: $scopeType, scopeKey: $scopeKey, period: $period,
                    page: $page, perPage: $perPage) {
      totalEntries
      entries {
        ...PeakLeaderboardRow
      }
      viewerEntry {
        ...PeakLeaderboardRow
      }
    }
  }

  fragment PeakLeaderboardRow on PeakLeaderboardEntry {
    rank
    verifiedCount
    listBaggedCount
    listPeaksCount
    user {
      id
      firebaseUserId
      displayName
      fullName
      avatar {
        lgUrl
      }
    }
  }
`

// The leaderboard's list picker: the viewer's lists, then every list. The same two peakLists
// calls the apps' GetMyPeaks makes for it, plus what the header needs to restyle itself for the
// list picked. Not webPath: it costs a query per list, and this is every list there is.
export const LEADERBOARD_LISTS_QUERY = `
  query PeakBaggingLeaderboardLists {
    mine: peakLists(mineOnly: true) {
      ...LeaderboardPickerList
    }
    all: peakLists {
      ...LeaderboardPickerList
    }
  }

  fragment LeaderboardPickerList on PeakList {
    slug
    name
    regionLabel
    locationLabel
    peaksCount
    baggedCount
    highlightColor
    baggedBadgeUrl
    nonBaggedBadgeUrl
    countries {
      slug
    }
  }
`

// The address of the one list picked, for the page's URL and its back link.
export const LEADERBOARD_LIST_PATH_QUERY = `
  query PeakBaggingLeaderboardListPath($slug: String!) {
    peakList(slug: $slug) {
      slug
      webPath
    }
  }
`

// Global search's Peak Lists section (apps/search). Here rather than beside
// Search.vue so spec/graphql/peaks/web_queries_spec.rb validates it with the
// other peak documents. Three rows: with nothing typed, the walker's recently
// bagged lists (mineOnly), then the ones near them (lat/lon), then the curated
// catalogue — see loadSearchPeakLists; typed, the catalogue matching the text.
// `totalCount` decides whether "View all" shows.
export const SEARCH_PEAK_LISTS_QUERY = `
  query PeakBaggingSearchPeakLists($query: String, $mineOnly: Boolean, $lat: Float, $lon: Float, $page: Int, $perPage: Int) {
    searchPeakLists(query: $query, mineOnly: $mineOnly, lat: $lat, lon: $lon, page: $page, perPage: $perPage) {
      totalCount
      peakLists {
        id
        slug
        name
        locationLabel
        webPath
        peaksCount
        baggedCount
        baggedBadgeUrl
        nonBaggedBadgeUrl
      }
    }
  }
`

// The viewer's bagged peaks, for colouring peak pucks on the main map's POI
// layer (BaseMapDataPOIsModule). The same field the apps sync from; un-bagged
// peaks come back as tombstones (`deletedAt` set) and are dropped client-side.
// Anonymous viewers get an empty list.
export const USER_BAGGED_PEAK_IDS_QUERY = `
  query PeakBaggingUserBaggedPeakIds {
    userBaggedPeaks {
      amenityId
      deletedAt
    }
  }
`
